import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeSetting, applyThemeToDocument, removeThemeFromDocument } from "@/lib/theme";

interface ThemeContextType {
  currentTheme: Partial<ThemeSetting> | null;
  refreshTheme: () => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeContextProvider");
  }
  return context;
};

export const ThemeContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentTheme, setCurrentTheme] = useState<Partial<ThemeSetting> | null>(null);

  const refreshTheme = useCallback(async (): Promise<void> => {
    try {
      const raw = localStorage.getItem('h365-active-theme');
      const cachedTheme = raw
        ? JSON.parse(raw) as Partial<ThemeSetting>
        : null;
      setCurrentTheme(cachedTheme);
      if (cachedTheme) {
        const isDark = document.documentElement.classList.contains('dark');
        applyThemeToDocument(cachedTheme, isDark);
      } else {
        removeThemeFromDocument();
      }
    } catch {
      setCurrentTheme(null);
      removeThemeFromDocument();
    }
  }, []);

  useEffect(() => {
    refreshTheme();
  }, [refreshTheme]);

  /** Listen for dark mode class changes to re-apply correct colors */
  useEffect(() => {
    if (!currentTheme) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          applyThemeToDocument(currentTheme, isDark);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, [currentTheme]);

  const contextValue = useMemo(
    () => ({ currentTheme, refreshTheme, isLoading: false }),
    [currentTheme, refreshTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
